import React, { Suspense, lazy, ComponentType } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Skeleton } from '@/components/ui/skeleton';
import { lazyRetryWithAutoRefresh, isChunkLoadError } from '@/utils/lazyRetry';
import { getEnvironment } from '@/utils/previewCacheGuard';

interface LazyPageLoaderProps {
  children: React.ReactNode;
  fallback?: React.ComponentType;
  errorFallback?: React.ComponentType<{ error: Error; resetErrorBoundary: () => void }>;
}

const recoverableResetAttempts = new Map<string, number>();

const isTransientReactDomError = (error: Error): boolean => {
  const msg = error?.message || '';
  const name = error?.name || '';
  return (
    name === 'NotFoundError' ||
    msg.includes("Failed to execute 'removeChild'") ||
    msg.includes("Failed to execute 'insertBefore'") ||
    msg.includes('is not a child of this node')
  );
};

const isRecoverableReactRuntimeError = (error: Error): boolean => {
  const msg = error?.message || '';
  return (
    msg.includes('Minified React error #321') ||
    msg.includes('Invalid hook call')
  );
};

const DefaultErrorFallback: React.FC<{ error: Error; resetErrorBoundary: () => void }> = ({ 
  error, 
  resetErrorBoundary 
}) => {
  const transient = isTransientReactDomError(error);
  const recoverableRuntime = isRecoverableReactRuntimeError(error);
  const lastResetRef = React.useRef<number>(0);

  // Tentativa de recuperação automática segura para erros transitórios/recuperáveis
  React.useEffect(() => {
    const now = Date.now();
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const resetKey = `${pathname}:${error?.message || error?.name || 'unknown'}`;
    const attempts = recoverableResetAttempts.get(resetKey) || 0;
    // Limite de 1 tentativa a cada 5 segundos para evitar loops infinitos se o erro persistir
    if ((transient || recoverableRuntime) && attempts < 2 && now - lastResetRef.current > 5000) {
      lastResetRef.current = now;
      recoverableResetAttempts.set(resetKey, attempts + 1);
      const timer = setTimeout(() => {
        console.warn('[LazyPageLoader] Tentando recuperação automática de erro recuperável...');
        resetErrorBoundary();
      }, recoverableRuntime ? 100 : 500);
      return () => clearTimeout(timer);
    }
  }, [transient, recoverableRuntime, error, resetErrorBoundary]);

  // Log sempre — independente de mostrar UI ou não
  console.error('[LazyPageLoader] Erro capturado:', error);
  console.warn('[LazyPageLoader] STACK:', error?.stack);
  try {
    (window as any).__LAST_LAZY_ERROR = {
      message: error?.message,
      stack: error?.stack,
      pathname: typeof window !== 'undefined' ? window.location.pathname : '',
      transient,
      recoverableRuntime,
      when: new Date().toISOString(),
    };
  } catch { /* ignore */ }

  // Para erros transitórios do reconciler (removeChild/insertBefore), renderizamos null
  // para evitar espaços em branco (gaps) enquanto a recuperação automática não acontece.
  if (transient || recoverableRuntime) {
    return null;
  }

  const isChunkError = isChunkLoadError(error);

  // Mostrar detalhes em DEV puro OU em qualquer ambiente que não seja produção (preview do Lovable, etc.)
  const env = (() => {
    try { return getEnvironment(); } catch { return 'production' as const; }
  })();
  const showDetails = import.meta.env.DEV || env !== 'production';
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';

  const handleRetry = () => {
    if (isChunkError) {
      // Forçar hard reload com cache-busting para escapar de HTML/chunks obsoletos em cache
      try {
        sessionStorage.removeItem('chunk-refresh-attempted');
        if ('caches' in window) {
          caches.keys().then((names) => Promise.all(names.map((n) => caches.delete(n)))).finally(() => {
            const url = new URL(window.location.href);
            url.searchParams.set('cacheBust', Date.now().toString());
            window.location.replace(url.toString());
          });
          return;
        }
      } catch { /* ignore */ }
      const url = new URL(window.location.href);
      url.searchParams.set('cacheBust', Date.now().toString());
      window.location.replace(url.toString());
    } else {
      resetErrorBoundary();
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
      <div className="text-center space-y-4 w-full max-w-3xl">
        <h2 className="text-2xl font-semibold text-destructive">Erro ao carregar página</h2>
        <p className="text-muted-foreground">
          {isChunkError 
            ? 'Ocorreu um erro ao carregar recursos. Vamos atualizar a página para corrigir.'
            : 'Ocorreu um erro inesperado. Tente novamente.'
          }
        </p>

        {showDetails && (
          <div className="mt-4 p-4 bg-destructive/10 border border-destructive/30 rounded-md text-left max-w-3xl mx-auto">
            <p className="font-mono text-xs text-muted-foreground mb-2">
              <strong>Rota:</strong> {pathname || '(desconhecida)'}
            </p>
            <p className="font-mono text-sm text-destructive break-words">
              <strong>Erro:</strong> {error.message || '(sem mensagem)'}
            </p>
            {isChunkError && (
              <p className="mt-1 text-xs text-muted-foreground italic">
                (detectado como chunk error — possível cache antigo do navegador)
              </p>
            )}
            {error.stack && (
              <pre className="mt-2 text-xs text-muted-foreground overflow-auto max-h-60 whitespace-pre-wrap">
                {error.stack}
              </pre>
            )}
            <p className="mt-2 text-[10px] text-muted-foreground">
              Dica: rode <code>window.__LAST_LAZY_ERROR</code> no DevTools para copiar este erro.
            </p>
          </div>
        )}

        <button 
          onClick={handleRetry}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          {isChunkError ? 'Atualizar Página' : 'Tentar novamente'}
        </button>
      </div>
    </div>
  );
};

const DefaultFallback: React.FC = () => (
  <div className="space-y-6 p-6">
    <Skeleton className="h-8 w-48" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[...Array(6)].map((_, i) => (
        <Skeleton key={i} className="h-32 w-full" />
      ))}
    </div>
  </div>
);

export const LazyPageLoader: React.FC<LazyPageLoaderProps> = ({
  children,
  fallback: FallbackComponent = DefaultFallback,
  errorFallback: ErrorFallbackComponent = DefaultErrorFallback,
}) => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallbackComponent}>
      <Suspense fallback={<FallbackComponent />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
};

// Higher-order component para criar páginas lazy com loader otimizado e retry logic
export const createLazyPage = (
  importFn: () => Promise<{ default: ComponentType<any> }>,
  fallback?: React.ComponentType,
  errorFallback?: React.ComponentType<{ error: Error; resetErrorBoundary: () => void }>
) => {
  // Usar retry robusto com auto-refresh para erros de chunk loading
  const LazyComponent = lazy(() => lazyRetryWithAutoRefresh(importFn));
  
  return (props: any) => (
    <LazyPageLoader fallback={fallback} errorFallback={errorFallback}>
      <LazyComponent {...props} />
    </LazyPageLoader>
  );
};

// Hook para preload de componentes lazy
export const usePreloadPage = () => {
  const preloadPage = React.useCallback((importFn: () => Promise<any>) => {
    // Preload com timeout para não bloquear thread principal
    setTimeout(() => {
      importFn().catch(() => {
        // Ignorar erros de preload
      });
    }, 100);
  }, []);

  return preloadPage;
};